import {
    MediaPacksResponseItem,
    WorkflowWorkflowInfo,
} from '@calm/evexia-web-sdk';
import planMessages from '@rhg/lang/messages/plan';
import {
    associateBy,
    associateByKey,
    getRandomArrayElement,
} from '@rhg/utils/arrays';
import { ProgramState, getClinicalProgramState } from '@rhg/utils/programs';
import { CheckinTypeKey } from '@rhg/utils/usercrypt/checkins';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useActiveCircleConditions } from '../use-circle-conditions';
import { useActiveCircleClinicalPacks } from '../use-clinical-packs';
import { useIsMobile } from '../use-is-mobile';
import { useLatestCheckinEntry } from '../use-latest-checkin-entry';
import { useActiveCircleRecommendations } from '../use-recommendations';
import { useHasTakenScreening } from '../use-screening-results';
import { useWorkflowsForActiveCircle } from '../use-workflows';

const topRecommendationInProgressHeaders = [
    planMessages['plan.top_recommendation_joined_header.1'],
    planMessages['plan.top_recommendation_joined_header.2'],
    planMessages['plan.top_recommendation_joined_header.3'],
    planMessages['plan.top_recommendation_joined_header.4'],
    planMessages['plan.top_recommendation_joined_header.5'],
];

const topRecommendationInProgressSubHeaders = [
    planMessages['plan.top_recommendation_joined_subheader.1'],
    planMessages['plan.top_recommendation_joined_subheader.2'],
    planMessages['plan.top_recommendation_joined_subheader.3'],
    planMessages['plan.top_recommendation_joined_subheader.4'],
];

const topRecommendationRetakeHeaders = [
    planMessages['plan.top_recommendation_retake_header.1'],
    planMessages['plan.top_recommendation_retake_header.2'],
    planMessages['plan.top_recommendation_retake_header.3'],
    planMessages['plan.top_recommendation_retake_header.4'],
    planMessages['plan.top_recommendation_retake_header.5'],
];

const topRecommendationRetakeSubHeaders = [
    planMessages['plan.top_recommendation_retake_subheader.1'],
    planMessages['plan.top_recommendation_retake_subheader.2'],
    planMessages['plan.top_recommendation_retake_subheader.3'],
];

interface Heading {
    header: string;
    subHeader: string;
}

export function useCarePlanTopRecommendationSection() {
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile('lg');
    const { latestEntrySummary: latestGoals } = useLatestCheckinEntry(
        CheckinTypeKey.Goal
    );
    const { latestEntrySummary: latestTopics } = useLatestCheckinEntry(
        CheckinTypeKey.Topic
    );
    const { conditions } = useActiveCircleConditions();
    const firstGoalId = latestGoals?.latestEntry?.checkins.values?.[0].id;
    const firstTopicId = latestTopics?.latestEntry?.checkins.values?.[0].id;

    const { programs, isLoading: areRecommendationsLoading } =
        useActiveCircleRecommendations();

    const recommendations = useMemo(
        () =>
            (programs ?? []).filter(
                ({ additionalData }) =>
                    !!additionalData.workflowId && !!additionalData.packClassId
            ),
        [programs]
    );
    const workflowIds = useMemo(
        () =>
            recommendations.map(
                ({ additionalData }) => additionalData.workflowId!
            ),
        [recommendations]
    );
    const packClasses = useMemo(
        () =>
            recommendations.map(
                ({ additionalData }) => additionalData.packClassId!
            ),
        [recommendations]
    );
    const workflowsResponse = useWorkflowsForActiveCircle(workflowIds);
    const { clinicalPacks, isLoading: arePacksLoading } =
        useActiveCircleClinicalPacks(packClasses);

    const clinicalPacksRecord = useMemo(
        () => associateByKey(clinicalPacks?.items ?? [], 'packClassId'),
        [clinicalPacks?.items]
    );

    const workflowsRecord = useMemo(
        () =>
            associateBy(
                workflowsResponse
                    .map(({ data }) => data?.[0])
                    .filter(
                        (item) => typeof item !== 'undefined'
                    ) as WorkflowWorkflowInfo[],
                (item) => item.workflowDefn.defnId
            ),
        [workflowsResponse]
    );

    /**
     * A record to hold both program & workflow status information.
     */
    const programsRecord = useMemo(
        () =>
            packClasses.reduce<
                Record<
                    string,
                    MediaPacksResponseItem & {
                        workflow: WorkflowWorkflowInfo;
                        state: ProgramState;
                    }
                >
            >((acc, curr, index) => {
                // We rely on the fact that `packClasses` and `workflowIds` are parallel arrays.
                const workflowId = workflowIds[index];
                if (
                    curr in clinicalPacksRecord &&
                    workflowId in workflowsRecord
                ) {
                    acc[curr] = {
                        ...clinicalPacksRecord[curr],
                        workflow: workflowsRecord[workflowId],
                        state: getClinicalProgramState(
                            workflowsRecord[workflowId]
                        ),
                    };
                }

                return acc;
            }, {}),
        [clinicalPacksRecord, packClasses, workflowIds, workflowsRecord]
    );

    const hasGoals = useMemo(
        () => firstGoalId !== 'none_selected',
        [firstGoalId]
    );
    const hasTopics = useMemo(
        () => firstTopicId !== 'none_selected',
        [firstTopicId]
    );
    const hasConditions = useMemo(
        () => !!conditions?.conditions?.length,
        [conditions?.conditions?.length]
    );
    const isMentalHealthScreeningTaken = useHasTakenScreening();

    const topRecommendationState = useMemo(
        () => Object.values(programsRecord)[0]?.state,
        [programsRecord]
    );
    const topRecommendationProgram = useMemo(
        () => Object.values(programsRecord)[0]?.program,
        [programsRecord]
    );
    const topRecommendationWorkflow = useMemo(
        () => Object.values(programsRecord)[0]?.workflow,
        [programsRecord]
    );
    const topRecommendationPackProgram = useMemo(
        () => Object.values(programsRecord)[0]?.packProgram,
        [programsRecord]
    );
    const topRecommendationPackClass = useMemo(
        () => Object.keys(programsRecord)[0],
        [programsRecord]
    );
    const topRecommendation = useMemo(
        () =>
            recommendations.find(
                ({ additionalData }) =>
                    !!additionalData.packClassId &&
                    additionalData.packClassId in programsRecord
            ),
        [programsRecord, recommendations]
    );
    const otherEnrolledPrograms = useMemo(
        () =>
            Object.values(programsRecord)
                .filter(
                    ({ state, packClassId }) =>
                        packClassId !== topRecommendationPackClass &&
                        state === ProgramState.InProgress
                )
                .slice(0, 2),
        [programsRecord, topRecommendationPackClass]
    );

    const otherEnrolledProgramsStringCopy = useMemo(
        () =>
            otherEnrolledPrograms
                .map(({ program }) => program?.title)
                .filter((title) => !!title)
                .join(', '),
        [otherEnrolledPrograms]
    );

    const isLoading = useMemo(
        () => areRecommendationsLoading || arePacksLoading,
        [arePacksLoading, areRecommendationsLoading]
    );

    const isTopRecommendationCardDisplayed = useMemo(
        () =>
            !!topRecommendation?.additionalData.packClassId &&
            !!topRecommendationPackProgram?.programId &&
            !!topRecommendationProgram?.id &&
            !!topRecommendationWorkflow.workflowDefn.defnId,

        [
            topRecommendation?.additionalData.packClassId,
            topRecommendationPackProgram?.programId,
            topRecommendationProgram?.id,
            topRecommendationWorkflow?.workflowDefn.defnId,
        ]
    );

    const hasSkippedFtue = useMemo(
        () =>
            !hasConditions &&
            !hasGoals &&
            !hasTopics &&
            !isMentalHealthScreeningTaken,
        [hasConditions, hasGoals, hasTopics, isMentalHealthScreeningTaken]
    );

    const recommendationsCount = useMemo(
        () => Object.keys(programsRecord).length,
        [programsRecord]
    );

    const heading = useMemo<Heading>(() => {
        if (recommendations.length === 0) {
            if (
                !hasGoals &&
                !hasTopics &&
                !hasConditions &&
                !isMentalHealthScreeningTaken
            ) {
                return {
                    header: formatMessage(
                        planMessages['plan.no_recommendation_header.1']
                    ),
                    subHeader: formatMessage(
                        planMessages['plan.no_recommendation_subheader.1']
                    ),
                };
            }

            return {
                header: formatMessage(
                    planMessages['plan.no_recommendation_header.2']
                ),
                subHeader: formatMessage(
                    planMessages['plan.no_recommendation_subheader.2']
                ),
            };
        }

        if (topRecommendationState === ProgramState.InProgress) {
            return {
                header: formatMessage(
                    getRandomArrayElement(topRecommendationInProgressHeaders)
                ),
                subHeader: formatMessage(
                    getRandomArrayElement(
                        topRecommendationInProgressSubHeaders
                    ),
                    {
                        programTitle: topRecommendationProgram?.title,
                    }
                ),
            };
        }

        if (otherEnrolledProgramsStringCopy) {
            return {
                header: formatMessage(planMessages['plan.next_up_header'], {
                    programTitle: topRecommendationProgram?.title,
                }),
                subHeader: formatMessage(
                    planMessages['plan.next_up_subheader'],
                    {
                        joinedPrograms: otherEnrolledProgramsStringCopy,
                    }
                ),
            };
        }

        if (topRecommendationState === ProgramState.Completed) {
            return {
                header: formatMessage(
                    getRandomArrayElement(topRecommendationRetakeHeaders)
                ),
                subHeader: formatMessage(
                    getRandomArrayElement(topRecommendationRetakeSubHeaders),
                    {
                        programTitle: topRecommendationProgram?.title,
                    }
                ),
            };
        }

        return {
            header: formatMessage(planMessages['plan.start_with_header'], {
                programTitle: topRecommendationProgram?.title,
            }),
            subHeader: formatMessage(planMessages['plan.start_with_subheader']),
        };
    }, [
        hasConditions,
        hasGoals,
        hasTopics,
        isMentalHealthScreeningTaken,
        otherEnrolledProgramsStringCopy,
        recommendations.length,
        formatMessage,
        topRecommendationProgram?.title,
        topRecommendationState,
    ]);

    const isCtaButtonDisplayed = useMemo(
        () =>
            isMobile &&
            isTopRecommendationCardDisplayed &&
            topRecommendationState !== ProgramState.InProgress &&
            !otherEnrolledProgramsStringCopy,
        [
            isMobile,
            isTopRecommendationCardDisplayed,
            otherEnrolledProgramsStringCopy,
            topRecommendationState,
        ]
    );

    return {
        ...heading,
        isLoading,
        topRecommendationProgram,
        topRecommendationPackProgram,
        topRecommendation,
        topRecommendationWorkflow,
        topRecommendationState,
        hasJoinedOtherPrograms: !!otherEnrolledProgramsStringCopy,
        isTopRecommendationCardDisplayed,
        isCtaButtonDisplayed,
        hasSkippedFtue,
        recommendationsCount,
    };
}
